import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { Button, Box } from "@mui/material";

import { LOGINS_API_URLS, LoginsAPI } from "../../../../api/logins";
import { useCustomerProvider } from "../../../../context";
import { customerActions } from "../../../../reducers/customer";
import { ContainerWithLoader } from "../../../../components/ui";
import { SharedRegIpsTable } from "../index";
import usePagination from "../../../../hooks/usePagination";
import { ISortBy } from "../../../../types";

const SharedRegIps = (): React.ReactElement | null => {
  const params: { id: string } = useParams();
  const id = +params.id;
  const dispatch = useDispatch();
  const { customer } = useCustomerProvider();

  const [isOpenSharedIps, setIsOpenSharedIps] = useState(false);
  const handleOpenSharedIps = () => setIsOpenSharedIps(!isOpenSharedIps);

  const { sharedIps, sharedRegIp } = useSelector((state) => state.customer.ipsData);

  const sharedIpsPagination = usePagination();
  const [sortBySharedIps, setSortBySharedIps] = useState<ISortBy>({
    by: "timestamp",
    order: false,
  });
  const getSharedIpsRequest = useQuery({
    enabled: !!customer.reg_ip,
    retry: false,
    queryKey: [
      `${LOGINS_API_URLS.sharedIps}/${customer.reg_ip}`,
      id,
      customer.reg_ip,
      sharedIpsPagination.limit,
      sharedIpsPagination.page,
      sortBySharedIps,
    ],
    queryFn: async () => {
      dispatch(customerActions.fetchSharedIpsRequest());
      const { data } = await LoginsAPI.getSharedIpsRequest(customer.reg_ip, {
        accounts_id: +id,
        sortBy: {
          by: sortBySharedIps.by,
          order: sortBySharedIps.order,
        },
        limit: sharedIpsPagination.limit,
        page: sharedIpsPagination.page,
      });
      dispatch(customerActions.fetchSharedIpsSuccess(data.sharedIps));
      sharedIpsPagination.setCount(data.sharedIps.count);
      return data;
    },
  });

  useEffect(() => {
    if (getSharedIpsRequest.isError && getSharedIpsRequest.error) {
      dispatch(customerActions.fetchSharedIpsFailed());
    }
  }, [getSharedIpsRequest.isError, getSharedIpsRequest.error]);

  const sharedRegIpsPagination = usePagination();
  const [sortBySharedRegIp, setSortSharedRegIp] = useState<ISortBy>({
    by: "joined",
    order: false,
  });
  const getSharedRegIpsRequest = useQuery({
    retry: false,
    enabled: !!customer.reg_ip,
    queryKey: [
      LOGINS_API_URLS.sharedRegIp,
      id,
      customer.reg_ip,
      sharedRegIpsPagination.limit,
      sharedRegIpsPagination.page,
      sortBySharedRegIp,
    ],
    queryFn: async () => {
      dispatch(customerActions.fetchSharedRegIpsRequest());
      const { data } = await LoginsAPI.getSharedRegIpsRequest(customer.reg_ip, +id, {
        sortBy: {
          by: sortBySharedRegIp.by,
          order: sortBySharedRegIp.order,
        },
        limit: sharedRegIpsPagination.limit,
        page: sharedRegIpsPagination.page,
      });
      dispatch(customerActions.fetchSharedRegIpsSuccess(data.sharedRegIp));
      sharedRegIpsPagination.setCount(data.sharedRegIp.count);
      return data;
    },
  });
  useEffect(() => {
    if (getSharedRegIpsRequest.isError && getSharedRegIpsRequest.error) {
      dispatch(customerActions.fetchSharedRegIpsFailed());
    }
  }, [getSharedRegIpsRequest.isError, getSharedRegIpsRequest.error]);

  const countSharedIps = sharedIps.data.count;
  const countSharedRegIp = sharedRegIp.data.count;

  const isLoading = sharedIps.loading || sharedRegIp.loading;

  if (!customer.reg_ip) {
    return null;
  }

  return (
    <Box mb="10px">
      <SharedRegIpsTable
        sharedIpsPagination={sharedIpsPagination}
        sharedRegIpsPagination={sharedRegIpsPagination}
        sortBySharedIps={[sortBySharedIps, setSortBySharedIps]}
        sortBySharedRegIp={[sortBySharedRegIp, setSortSharedRegIp]}
        open={isOpenSharedIps}
        handleClose={handleOpenSharedIps}
      />

      <ContainerWithLoader
        noResultsMessage=""
        isLoading={!isOpenSharedIps && isLoading}
        report={[...sharedRegIp.data.rows, sharedIps.data.rows]}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box mr="10px">
            {!!countSharedRegIp && (
              <p style={{ margin: 0 }}>
                Registration IP was used in <b>{countSharedRegIp}</b> other accounts for
                registration
              </p>
            )}
            {!!countSharedIps && (
              <p style={{ margin: 0 }}>
                Registration IP was used <b>{countSharedIps}</b> times for login in other accounts
              </p>
            )}
          </Box>

          {!!(countSharedRegIp || countSharedIps) && (
            <Button
              sx={{
                color: "#000",
                backgroundColor: "rgba(235, 109, 44, 0.61)",
                "&:hover": {
                  backgroundColor: "rgba(235, 109, 44, 0.61)",
                },
              }}
              color="secondary"
              onClick={handleOpenSharedIps}
            >
              Get info
            </Button>
          )}
        </Box>
      </ContainerWithLoader>
    </Box>
  );
};

export default SharedRegIps;
